import React, { useState, useRef, useEffect } from "react";
import {
  Button,
  Grid,
  Typography,
  Paper,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  TextField,
  FormControlLabel,
  Checkbox,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from "@material-ui/core";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import PersonAddIcon from "@material-ui/icons/PersonAdd";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import SendIcon from "@material-ui/icons/Send";
import ArrowForwardIcon from "@material-ui/icons/ArrowForward";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import RefreshIcon from "@material-ui/icons/Refresh";
import { Alert } from "@material-ui/lab";
import { API } from "aws-amplify";
import { LoadingSpinner } from "../../../utils/LoadingSpinner";
import { ValidationUtils } from "../utils/ValidationUtils";
import { parseMailingAddress } from "../utils/AddressParser";
import PatientDetailsForm from "../components/PatientDetailsForm";
import PlaceOfServiceSelector from "../components/PlaceOfServiceSelector";
import PrescriberSelector from "../components/PrescriberSelector";
import MedicationSelector from "../components/MedicationSelector";
import PharmacyDetails from "../components/PharmacyDetails";
import { buildNewRxPayload } from "../utils/PayloadBuilders";
import {
  useS3Operations,
  useUPMSIngestion,
  useCustomerCreation,
  useCustomerOptions,
  usePrescriptionForm,
} from "../hooks";
import { COLORS, BUTTON_STYLES } from "../styles/theme";
import { DataAccordion, FollowUpResultCard, StyledActionButton, HealthProfileSection, OneMedicalPrescriptionField } from "../components";
import { PREDEFINED_ADDRESSES } from "../config/addresses";
import CcsStatusUpdateSection from "../../prescription_lifecycle/components/prescription/CcsStatusUpdateSection";
import { IFollowUpIssueInput } from "../../prescription_lifecycle/components/prescription/followUp";
import RphDecisionApprovalSection from "../../pacman/components/rph_decision/RphDecisionApprovalSection";
import OrderSection from "../../update_pharmacy_customer/components/order/components/create_order/OrderSection";

interface IProps {
  data?: any;
}

const UnifiedNewRxWorkflowSection: React.FC<IProps> = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [emailPrefix, setEmailPrefix] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  // When checked, the prescription is treated as a One Medical prescription:
  // reveals the required Prescriber Order Number field and forces ClinicName to
  // "One Medical" in the payload.
  const [isOneMedical, setIsOneMedical] = useState(false);
  const [showS3Retrieval, setShowS3Retrieval] = useState(false);
  const [s3FileName, setS3FileName] = useState("");
  const [isUpdatingFields, setIsUpdatingFields] = useState(false);
  const [selectedAddressIndex, setSelectedAddressIndex] = useState<number>(0);
  const [showCcsStatusUpdate, setShowCcsStatusUpdate] = useState(false);
  // Which CCS action step 4 is running: approve, or route to follow-up instead.
  const [ccsMode, setCcsMode] = useState<"approve" | "followUp">("approve");
  const [followUpIssues, setFollowUpIssues] = useState<IFollowUpIssueInput[] | null>(null);
  const [ccsPrescriptionId, setCcsPrescriptionId] = useState("");
  const [isLookingUpPrescription, setIsLookingUpPrescription] = useState(false);
  // True when a CCS button was clicked but the prescription isn't ready yet (lookup
  // returned none); shows a retry message instead of opening the form.
  const [ccsNotReady, setCcsNotReady] = useState(false);
  const [showRphDecision, setShowRphDecision] = useState(false);
  const [showCreateOrder, setShowCreateOrder] = useState(false);
  // Controls the completed step-4 accordion so it can be collapsed when the user
  // moves on to Create Order (step 5). Defaults open since step 4 auto-expands on
  // completion.
  const [step4Expanded, setStep4Expanded] = useState(true);
  // Success responses for the follow-up steps; once set, the inline form is
  // replaced by a green completed-summary accordion (like the 3 summary steps).
  const [ccsResponse, setCcsResponse] = useState<any>(null);
  const [rphResponse, setRphResponse] = useState<any>(null);
  const [orderResponse, setOrderResponse] = useState<any>(null);

  const step1Ref = useRef<HTMLDivElement>(null);
  const step2Ref = useRef<HTMLDivElement>(null);
  const step3Ref = useRef<HTMLDivElement>(null);

  // Shared style for the Step 4 / Step 5 action buttons (orange outline).
  const nextStepButtonStyle = BUTTON_STYLES.nextStep;

  // Use our custom hooks
  const {
    isUploading,
    isRetrieving,
    uploadResponse,
    retrievedData,
    error: s3Error,
    uploadToS3,
    retrieveFromS3,
  } = useS3Operations();

  const {
    isIngesting,
    upmsResponse,
    error: upmsError,
    ingestNewRx,
  } = useUPMSIngestion();

  const {
    isEnrolling,
    enrollmentResponse,
    error: customerError,
    createCustomer,
    getCustomerAddresses,
    getAddressIdForZipCode,
  } = useCustomerCreation();

  const {
    options,
    handlePrimeChange,
    handleRxPassSubscriptionChange,
    handleRxPassForceActiveChange,
    handleHornbillSubscriptionChange,
    handleSetDefaultAddressChange,
    handleCommonAllergiesChange,
    handleCommonHealthConditionsChange,
    handlePregnantOrNursingChange,
    handleGenderChange,
  } = useCustomerOptions({ setDefaultAddress: true });

  const {
    patientDetails,
    prescriberDetails,
    medicationDetails,
    selectedPrescriber,
    selectedMedication,
    placeOfService,
    prescriberOrderNumber,
    fieldErrors,
    setPatientField,
    updatePatientDetails,
    handlePrescriberChange,
    handleMedicationChange,
    handleMedicationFieldChange,
    handlePlaceOfServiceChange,
    setPrescriberOrderNumber,
    updateFieldErrors,
  } = usePrescriptionForm("atorvastatin");

  // Scroll to active step when it changes
  useEffect(() => {
    const scrollToStep = () => {
      let ref: React.RefObject<HTMLDivElement>;
      switch (activeStep) {
        case 0:
          ref = step1Ref;
          break;
        case 1:
          ref = step2Ref;
          break;
        case 2:
          ref = step3Ref;
          break;
        default:
          return;
      }

      setTimeout(() => {
        ref.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 100);
    };

    scrollToStep();
  }, [activeStep]);

  const buildPrescriptionData = () => {
    return buildNewRxPayload({
      patientDetails,
      prescriberDetails,
      medicationDetails,
      placeOfService,
      prescriberOrderNumber: isOneMedical ? prescriberOrderNumber : undefined,
      isOneMedical,
    });
  };

  const handleCreateCustomer = async () => {
    try {
      // Determine which address to use
      let residentialAddressId: string | undefined;
      if (
        selectedAddressIndex >= 0 &&
        selectedAddressIndex < PREDEFINED_ADDRESSES.length
      ) {
        const selectedAddress = PREDEFINED_ADDRESSES[selectedAddressIndex];
        // Get address ID for the selected zipCode
        const addressId = await getAddressIdForZipCode(selectedAddress.zipCode);
        residentialAddressId = addressId || undefined;
      }

      const response = await createCustomer({
        emailPrefix: emailPrefix.trim() || undefined,
        phoneNumber: phoneNumber.trim() || undefined,
        residentialAddressId,
        options,
      });

      // Show loading indicator while fetching and updating fields
      setIsUpdatingFields(true);

      // Fetch and update patient details from customer
      const personId = response.profile?.[0]?.personId;
      if (personId && response.profile && response.profile[0]) {
        const addresses = await getCustomerAddresses(personId);

        const profile = response.profile[0];
        const demographicProfile = profile.demographicProfile;

        if (demographicProfile) {
          const mailingAddress = addresses[0]?.mailingAddress || "";
          const parsedAddress = parseMailingAddress(mailingAddress);

          const genderCode =
            demographicProfile.gender === "Male"
              ? "M"
              : demographicProfile.gender === "Female"
              ? "F"
              : demographicProfile.gender || "M";

          updatePatientDetails({
            firstName: demographicProfile.legalFirstName || "",
            lastName: demographicProfile.legalLastName || "",
            dateOfBirth: demographicProfile.dateOfBirth || "",
            gender: genderCode,
            phoneNumber: profile.phoneNumber || response.phoneNumber || "",
            addressLine1: parsedAddress.addressLine1,
            addressLine2: parsedAddress.addressLine2,
            city: parsedAddress.city,
            state: parsedAddress.state,
            zipCode: parsedAddress.zipCode,
          });

          // Hide loading indicator after fields are updated
          setIsUpdatingFields(false);
        } else {
          setIsUpdatingFields(false);
        }
      } else {
        setIsUpdatingFields(false);
      }
    } catch (err) {
      // Error is already handled by the hook
      setIsUpdatingFields(false);
    }
  };

  const handleUploadToS3 = async () => {
    const patientValidation = ValidationUtils.validatePatientDetails(patientDetails);
    const medicationValidation = ValidationUtils.validateMedicationDetails({
      daysSupply: medicationDetails.daysSupply,
      ndc: medicationDetails.ndc,
      sigText: medicationDetails.sigText,
      quantity: medicationDetails.quantity,
      writtenDate: medicationDetails.writtenDate,
    });

    // Combine field errors from both validations
    const combinedFieldErrors = {
      ...patientValidation.fieldErrors,
      ...medicationValidation.fieldErrors,
    };

    // One Medical prescriptions must carry the real OM order number — it is
    // matched against the prescription prescribed at One Medical, so it cannot
    // be auto-generated.
    const isOneMedicalPonMissing = isOneMedical && !prescriberOrderNumber.trim();
    if (isOneMedicalPonMissing) {
      combinedFieldErrors.prescriberOrderNumber =
        "Prescriber Order Number is required for One Medical prescriptions.";
    }

    updateFieldErrors(combinedFieldErrors);

    if (!patientValidation.isValid || !medicationValidation.isValid || isOneMedicalPonMissing) {
      return;
    }

    try {
      const prescriptionData = buildPrescriptionData();
      const response = await uploadToS3(prescriptionData);

      setS3FileName(
        response.s3Key || response.fileName || "uploaded_file.json"
      );
    } catch (err) {
      // Error is already handled by the hook
    }
  };

  const handleToggleS3Retrieval = async () => {
    if (!showS3Retrieval && !retrievedData) {
      try {
        await retrieveFromS3(s3FileName);
      } catch (err) {
        // Error is already handled by the hook
      }
    }
    setShowS3Retrieval(!showS3Retrieval);
  };

  const handleIngestToUPMS = async () => {
    try {
      // Retrieve from S3 if not already retrieved
      let dataToIngest = retrievedData;
      if (!dataToIngest) {
        dataToIngest = await retrieveFromS3(s3FileName);
      }

      // Ingest to UPMS
      await ingestNewRx(dataToIngest, s3FileName);
    } catch (err) {
      // Error is already handled by the hook
    }
  };

  const handleReset = () => {
    // Reload the page to reset all state including hook states
    window.location.reload();
  };

  const renderStep1 = () => {
    const hasCustomer = !!enrollmentResponse;
    const hasS3Upload = !!uploadResponse;

    return (
      <div ref={step1Ref}>
        <Grid container spacing={3}>
          {!hasCustomer ? (
            <>
              <Grid item xs={12}>
                <Paper style={{ padding: 24, backgroundColor: "#e3f2fd" }}>
                  <Typography variant="h6" gutterBottom>
                    Step 1a: Create Pharmacy Customer
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Auto-generate a new pharmacy customer with random data
                  </Typography>
                </Paper>
              </Grid>

              <Grid item xs={12}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Email Prefix (Optional)"
                      variant="outlined"
                      value={emailPrefix}
                      onChange={(e) => setEmailPrefix(e.target.value)}
                      placeholder="e.g., john.doe"
                      helperText="Leave empty to auto-generate"
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Phone Number (Optional)"
                      variant="outlined"
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value)}
                      placeholder="e.g., 1234567890"
                      helperText="Leave empty to auto-generate"
                      fullWidth
                    />
                  </Grid>
                </Grid>
              </Grid>

              <Grid item xs={12}>
                <FormControl variant="outlined" fullWidth>
                  <InputLabel>Select Address</InputLabel>
                  <Select
                    value={selectedAddressIndex}
                    onChange={(e) =>
                      setSelectedAddressIndex(e.target.value as number)
                    }
                    label="Select Address"
                  >
                    {PREDEFINED_ADDRESSES.map((address, index) => (
                      <MenuItem key={index} value={index}>
                        {address.addressLine1}
                        {address.addressLine2 && `, ${address.addressLine2}`}
                        {`, ${address.city}, ${address.stateName}, ${address.zipCode}`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Typography
                  variant="caption"
                  color="textSecondary"
                  style={{ marginTop: 4, display: "block" }}
                >
                  Select a predefined address for the customer
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Customer Options
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={options.prime}
                          onChange={(e) => handlePrimeChange(e.target.checked)}
                          color="primary"
                        />
                      }
                      label="Prime Member"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={options.rxPassSubscription}
                          onChange={(e) =>
                            handleRxPassSubscriptionChange(e.target.checked)
                          }
                          color="primary"
                        />
                      }
                      label="RxPass Subscription (requires Prime)"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={options.rxPassForceActive}
                          onChange={(e) =>
                            handleRxPassForceActiveChange(e.target.checked)
                          }
                          color="primary"
                        />
                      }
                      label="RxPass Force Active (requires Prime + RxPass)"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={options.hornbillSubscription}
                          onChange={(e) =>
                            handleHornbillSubscriptionChange(e.target.checked)
                          }
                          color="primary"
                        />
                      }
                      label="Hornbill Subscription"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={options.setDefaultAddress}
                          onChange={(e) =>
                            handleSetDefaultAddressChange(e.target.checked)
                          }
                          color="primary"
                        />
                      }
                      label="Set Default Address"
                    />
                  </Grid>
                </Grid>
              </Grid>

              <HealthProfileSection
                gender={options.gender}
                commonAllergies={options.commonAllergies}
                commonHealthConditions={options.commonHealthConditions}
                pregnantOrNursing={options.pregnantOrNursing}
                onGenderChange={handleGenderChange}
                onCommonAllergiesChange={handleCommonAllergiesChange}
                onCommonHealthConditionsChange={handleCommonHealthConditionsChange}
                onPregnantOrNursingChange={handlePregnantOrNursingChange}
              />

              {customerError && (
                <Grid item xs={12}>
                  <Alert severity="error">
                    <strong>Customer Creation Error:</strong> {customerError}
                  </Alert>
                </Grid>
              )}

              <Grid item xs={12}>
                <StyledActionButton
                  label="Create Customer"
                  loadingLabel="Creating Customer..."
                  onClick={handleCreateCustomer}
                  loading={isEnrolling}
                  disabled={!!enrollmentResponse}
                  color={COLORS.orange}
                  startIcon={<PersonAddIcon />}
                />
              </Grid>
            </>
          ) : !hasS3Upload ? (
            <>
              <Grid item xs={12}>
                <Alert severity="success">
                  Customer created! PersonId:{" "}
                  {enrollmentResponse?.profile?.[0]?.personId}
                </Alert>
              </Grid>

              <Grid item xs={12}>
                <Paper style={{ padding: 24, backgroundColor: "#fff3e0" }}>
                  <Typography variant="h6" gutterBottom>
                    Step 1b: Configure & Upload Prescription
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Patient details are auto-filled. Choose prescriber,
                    medication, and pharmacy.
                  </Typography>
                </Paper>
              </Grid>

              {isUpdatingFields && (
                <Grid item xs={12}>
                  <Alert severity="info">
                    ⏳ Updating patient details fields with customer
                    information...
                  </Alert>
                </Grid>
              )}

              <PatientDetailsForm
                patientDetails={patientDetails}
                onFieldChange={setPatientField}
                fieldErrors={fieldErrors}
              />

              <PlaceOfServiceSelector
                placeOfService={placeOfService}
                onPlaceOfServiceChange={handlePlaceOfServiceChange}
              />

              <OneMedicalPrescriptionField
                isOneMedical={isOneMedical}
                onIsOneMedicalChange={setIsOneMedical}
                prescriberOrderNumber={prescriberOrderNumber}
                onPrescriberOrderNumberChange={setPrescriberOrderNumber}
                fieldErrors={fieldErrors}
              />

              <PrescriberSelector
                selectedPrescriber={selectedPrescriber}
                prescriberDetails={prescriberDetails}
                onPrescriberChange={handlePrescriberChange}
              />

              <MedicationSelector
                selectedMedication={selectedMedication}
                medicationDetails={medicationDetails}
                onMedicationChange={handleMedicationChange}
                onMedicationFieldChange={handleMedicationFieldChange}
                fieldErrors={fieldErrors}
              />

              <PharmacyDetails />

              {s3Error && (
                <Grid item xs={12}>
                  <Alert severity="error">
                    <strong>S3 Upload Error:</strong> {s3Error}
                  </Alert>
                </Grid>
              )}

              <Grid item xs={12}>
                <StyledActionButton
                  label="Upload to S3"
                  loadingLabel="Uploading to S3..."
                  onClick={handleUploadToS3}
                  loading={isUploading}
                  disabled={!!uploadResponse}
                  color={COLORS.orange}
                  startIcon={<CloudUploadIcon />}
                />
              </Grid>
            </>
          ) : (
            <>
              <Grid item xs={12}>
                <Alert severity="success">
                  Prescription uploaded to S3! File: {s3FileName}
                </Alert>
              </Grid>

              <DataAccordion
                title="Customer Creation Response"
                data={enrollmentResponse}
                defaultExpanded
              />
              <DataAccordion
                title="S3 Upload Response"
                data={uploadResponse}
                defaultExpanded
              />

              <Grid item xs={12}>
                <StyledActionButton
                  label="Continue to UPMS Ingestion"
                  onClick={() => setActiveStep(1)}
                  color={COLORS.orange}
                  startIcon={<ArrowForwardIcon />}
                />
              </Grid>
            </>
          )}
        </Grid>

        {(isEnrolling || isUploading) && <LoadingSpinner />}
      </div>
    );
  };

  const renderStep2 = () => {
    const hasUPMSResponse = !!upmsResponse;

    return (
      <div ref={step2Ref}>
        <Grid container spacing={3}>
          {!hasUPMSResponse ? (
            <>
              <Grid item xs={12}>
                <Paper style={{ padding: 24, backgroundColor: "#e8f5e9" }}>
                  <Typography variant="h6" gutterBottom>
                    Ingest NewRx to UPMS
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Retrieve from S3 and ingest into UPMS system
                  </Typography>
                </Paper>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="body2">
                  <strong>S3 File:</strong> {s3FileName}
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Button
                  variant="text"
                  color="primary"
                  onClick={handleToggleS3Retrieval}
                  disabled={isRetrieving}
                >
                  {showS3Retrieval ? "Hide" : "Show"} S3 Payload (Optional)
                </Button>
              </Grid>

              {showS3Retrieval && retrievedData && (
                <DataAccordion title="Retrieved S3 Data" data={retrievedData} />
              )}

              {showS3Retrieval && !retrievedData && isRetrieving && (
                <Grid item xs={12}>
                  <LoadingSpinner />
                </Grid>
              )}

              {isRetrieving && !showS3Retrieval && (
                <Grid item xs={12}>
                  <Alert severity="info">
                    ⏳ Retrieving prescription data from S3...
                  </Alert>
                </Grid>
              )}

              {isIngesting && (
                <Grid item xs={12}>
                  <Alert severity="info">
                    ⏳ Ingesting prescription to UPMS...
                  </Alert>
                </Grid>
              )}

              {upmsError && (
                <Grid item xs={12}>
                  <Alert severity="error">
                    <strong>UPMS Ingestion Error:</strong> {upmsError}
                  </Alert>
                </Grid>
              )}

              {s3Error && !showS3Retrieval && (
                <Grid item xs={12}>
                  <Alert severity="error">
                    <strong>S3 Retrieval Error:</strong> {s3Error}
                  </Alert>
                </Grid>
              )}

              <Grid item xs={12}>
                <div style={{ display: "flex", gap: 16 }}>
                  <StyledActionButton
                    label="Ingest to UPMS"
                    loadingLabel={
                      isRetrieving
                        ? "Retrieving from S3..."
                        : "Ingesting to UPMS..."
                    }
                    onClick={handleIngestToUPMS}
                    loading={isIngesting || isRetrieving}
                    disabled={!!upmsResponse}
                    color={COLORS.orange}
                    startIcon={<SendIcon />}
                  />
                  <StyledActionButton
                    label="Back"
                    onClick={() => setActiveStep(0)}
                    disabled={isIngesting || isRetrieving || !!upmsResponse}
                    color={COLORS.orange}
                    startIcon={<ArrowBackIcon />}
                  />
                </div>
              </Grid>
            </>
          ) : (
            <>
              <Grid item xs={12}>
                <Alert severity="success">
                  Prescription successfully ingested to UPMS!
                </Alert>
              </Grid>

              <DataAccordion
                title="UPMS Ingestion Response"
                data={upmsResponse}
              />

              <Grid item xs={12}>
                <StyledActionButton
                  label="Complete Workflow"
                  onClick={() => setActiveStep(2)}
                  color={COLORS.orange}
                  startIcon={<ArrowForwardIcon />}
                />
              </Grid>
            </>
          )}
        </Grid>

        {(isIngesting || isRetrieving) && <LoadingSpinner />}
      </div>
    );
  };

  // Look up a prescriptionId for the new customer by personId, returning it (or ""
  // if none yet). The NewRx ingestion responses carry no prescriptionId, and
  // ingestion is async, so the prescription may not exist immediately after the
  // workflow completes — the caller shows a retry message in that case.
  //
  // This workflow creates exactly one prescription for the new customer, so the
  // sole returned element is the one to approve; we take the last element to be
  // robust if the list ever grows. The get-prescriptions response
  // (ListPrescriptionsResponse.prescriptions) has no timestamp field to sort by, so
  // we console.warn if more than one is unexpectedly returned, and CCS treats
  // prescriptionId as optional anyway (a blank value just approves all of them).
  const lookupPrescriptionId = async (): Promise<string> => {
    const personId = enrollmentResponse?.profile?.[0]?.personId;
    if (!personId) {
      return "";
    }

    setIsLookingUpPrescription(true);
    try {
      const response = await API.get(
        "ApaqPortalAPI",
        `/order/prescription/get-prescriptions?personId=${encodeURIComponent(personId)}`,
        {}
      );
      const prescriptions = response?.prescriptions || [];
      if (prescriptions.length > 1) {
        console.warn(
          `Found ${prescriptions.length} prescriptions for personId ${personId}; prefilling the most recent. Verify before approving.`
        );
      }
      const latest = prescriptions[prescriptions.length - 1];
      const prescriptionId = latest?.prescriptionId || "";
      if (prescriptionId) {
        setCcsPrescriptionId(prescriptionId);
      }
      return prescriptionId;
    } catch (error) {
      // Non-fatal: surface for debugging and treat as "not ready yet".
      console.warn("Failed to look up prescriptionId for CCS prefill:", error);
      return "";
    } finally {
      setIsLookingUpPrescription(false);
    }
  };

  // Toggle the inline CCS Status Update section in the given mode. The first time it
  // is opened we look up the prescriptionId; if none is ready yet, show a retry message
  // instead of the form (the customer's prescription is still being processed by UPMS).
  const handleToggleCcsStatusUpdate = async (mode: "approve" | "followUp") => {
    if (showCcsStatusUpdate) {
      setShowCcsStatusUpdate(false);
      return;
    }
    setCcsMode(mode);

    let prescriptionId = ccsPrescriptionId;
    if (!prescriptionId) {
      prescriptionId = await lookupPrescriptionId();
    }

    if (!prescriptionId) {
      setCcsNotReady(true);
      return;
    }

    setCcsNotReady(false);
    setShowCcsStatusUpdate(true);
  };

  // Step 4: Approve Prescription or Route to Follow-Up (CCS Status Update), or Approve
  // Rxless OTC (RPH Decision). Each reuses the existing section component pre-filled
  // from the new customer. An approval collapses into a summary accordion matching
  // steps 1-3; a follow-up ends the workflow on the follow-up result card.
  const renderStep4 = () => {
    const profile = enrollmentResponse?.profile?.[0];
    const personId = profile?.personId;
    const profileId = profile?.profileId;

    if (!personId && !profileId) {
      return null;
    }

    // Once an approval succeeds, step 4 is done: render it as a plain summary
    // accordion matching steps 1-3 (no orange action panel), titled by whichever
    // approval ran.
    if (ccsResponse) {
      return (
        <DataAccordion
          title="4. Approve Prescription"
          data={ccsResponse}
          expanded={step4Expanded}
          onExpandedChange={setStep4Expanded}
        />
      );
    }
    if (rphResponse) {
      return (
        <DataAccordion
          title="4. Approve Rxless OTC"
          data={rphResponse}
          expanded={step4Expanded}
          onExpandedChange={setStep4Expanded}
        />
      );
    }
    // A prescription in follow-up can't be ordered, so the workflow ends here.
    if (followUpIssues) {
      return (
        <FollowUpResultCard
          title="4. Routed to Follow-Up"
          prescriptionId={ccsPrescriptionId}
          issues={followUpIssues}
        />
      );
    }

    const ccsButtonLabel = (mode: "approve" | "followUp", label: string) =>
      ccsMode === mode && isLookingUpPrescription
        ? "Loading..."
        : ccsMode === mode && showCcsStatusUpdate
        ? "Hide"
        : label;
    const showCcsButton = (mode: "approve" | "followUp") =>
      !!personId && !showRphDecision && (!showCcsStatusUpdate || ccsMode === mode);

    return (
      <Grid item xs={12} style={{ width: "100%", maxWidth: 1200 }}>
        <Paper style={{ padding: 24, backgroundColor: "#fff3e0" }}>
          <Typography variant="h6" gutterBottom>
            4. Approve or Route to Follow-Up
          </Typography>

          <div style={{ marginBottom: 16 }}>
            <Alert severity="warning">
              Please continue with this step after 2 mins once step 3 above is
              done.
            </Alert>
          </div>

          <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
            {showCcsButton("approve") && (
              <Button
                variant="outlined"
                onClick={() => handleToggleCcsStatusUpdate("approve")}
                disabled={isLookingUpPrescription}
                style={nextStepButtonStyle}
              >
                {ccsButtonLabel("approve", "Approve Prescription")}
              </Button>
            )}
            {showCcsButton("followUp") && (
              <Button
                variant="outlined"
                onClick={() => handleToggleCcsStatusUpdate("followUp")}
                disabled={isLookingUpPrescription}
                style={nextStepButtonStyle}
              >
                {ccsButtonLabel("followUp", "Route to Follow-Up")}
              </Button>
            )}
            {profileId && !showCcsStatusUpdate && (
              <Button
                variant="outlined"
                onClick={() => {
                  // Clear any stale CCS "not ready" warning when pivoting to RPH.
                  setCcsNotReady(false);
                  setShowRphDecision((show) => !show);
                }}
                style={nextStepButtonStyle}
              >
                {showRphDecision ? "Hide" : "Approve Rxless OTC"}
              </Button>
            )}
          </div>

          {ccsNotReady && !showCcsStatusUpdate && (
            <div style={{ marginTop: 16 }}>
              <Alert severity="warning">
                Your prescription is not ready yet. Please click the button
                again after 2 mins once step 3 above is done.
              </Alert>
            </div>
          )}

          {showCcsStatusUpdate && personId && (
            <div style={{ marginTop: 24 }}>
              {/* Opened only once a prescriptionId is resolved, so it is seeded into
                  the constructor-backed field on mount. */}
              <CcsStatusUpdateSection
                mode={ccsMode}
                initialPersonId={personId}
                initialPrescriptionId={ccsPrescriptionId}
                submitColor={COLORS.orange}
                onSuccess={(response) => {
                  setCcsResponse(response);
                  setShowCcsStatusUpdate(false);
                  setCcsNotReady(false);
                }}
                onFollowUp={(_response, submittedPrescriptionId, issues) => {
                  setCcsPrescriptionId(submittedPrescriptionId);
                  setFollowUpIssues(issues);
                  setShowCcsStatusUpdate(false);
                  setCcsNotReady(false);
                }}
              />
            </div>
          )}

          {showRphDecision && profileId && (
            <div style={{ marginTop: 24 }}>
              <RphDecisionApprovalSection
                initialProfileId={profileId}
                submitColor={COLORS.orange}
                onSuccess={(response) => {
                  setRphResponse(response);
                  setShowRphDecision(false);
                }}
              />
            </div>
          )}
        </Paper>
      </Grid>
    );
  };

  // Item 5 — Create Order, reusing the Create Order section from
  // /update-pharmacy-customer, pre-filled with the new customer's personId so its
  // prescription/address/payment selectors auto-load. On success the inline form
  // collapses into a plain summary accordion matching steps 1-4. Hidden until item 4
  // (Approve Prescription or Approve Rxless OTC) has been processed successfully.
  const renderStep5 = () => {
    const personId = enrollmentResponse?.profile?.[0]?.personId;
    const step4Complete = !!ccsResponse || !!rphResponse;
    if (!personId || !step4Complete) {
      return null;
    }

    // Once the order succeeds, render it as a plain summary accordion matching
    // steps 1-4 (no orange action panel).
    if (orderResponse) {
      return (
        <DataAccordion
          title="5. Create Order"
          data={orderResponse}
          defaultExpanded
        />
      );
    }

    return (
      <Grid item xs={12} style={{ width: "100%", maxWidth: 1200 }}>
        <Paper style={{ padding: 24, backgroundColor: "#fff3e0" }}>
          <Typography variant="h6" gutterBottom>
            5. Create Order
          </Typography>

          <div style={{ marginBottom: 16 }}>
            <Alert severity="warning">
              Please continue with this step after 1 min once step 4 above is
              done.
            </Alert>
          </div>

          <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
            <Button
              variant="outlined"
              onClick={() => {
                // Collapse the completed step 4 when opening Create Order.
                if (!showCreateOrder) {
                  setStep4Expanded(false);
                }
                setShowCreateOrder((show) => !show);
              }}
              style={nextStepButtonStyle}
            >
              {showCreateOrder ? "Hide" : "Create Order"}
            </Button>
          </div>

          {showCreateOrder && (
            <div style={{ marginTop: 24 }}>
              <OrderSection
                initialPersonId={personId}
                initialPrescriptionId={ccsPrescriptionId}
                submitColor={COLORS.orange}
                onSuccess={(response: any) => {
                  setOrderResponse(response);
                  setShowCreateOrder(false);
                }}
              />
            </div>
          )}
        </Paper>
      </Grid>
    );
  };

  const renderStep3 = () => {
    return (
      <div ref={step3Ref}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Paper
              style={{
                padding: 32,
                backgroundColor: "#e8f5e9",
                textAlign: "center",
              }}
            >
              <Typography
                variant="h5"
                gutterBottom
                style={{ color: "#2e7d32" }}
              >
                ✓ Workflow Complete!
              </Typography>
              <Typography variant="body1" color="textSecondary">
                Customer created, prescription uploaded to S3, and ingested to
                UPMS successfully.
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              Summary
            </Typography>
          </Grid>

          <DataAccordion
            title="1. Customer Creation"
            data={enrollmentResponse}
          />
          <DataAccordion title="2. S3 Upload" data={uploadResponse} />
          <DataAccordion title="3. UPMS Ingestion" data={upmsResponse} />

          {renderStep4()}
          {renderStep5()}

          <Grid item xs={12}>
            <StyledActionButton
              label="Start New Workflow"
              onClick={handleReset}
              color={COLORS.orange}
              startIcon={<RefreshIcon />}
            />
          </Grid>
        </Grid>
      </div>
    );
  };

  return (
    <Grid container direction="column" justifyContent="center" spacing={3}>
      <Grid item xs={12} style={{ width: "100%" }}>
        <Stepper activeStep={activeStep} orientation="vertical">
          <Step>
            <StepLabel
              icon={
                enrollmentResponse && uploadResponse ? (
                  <CheckCircleIcon style={{ color: "#4caf50" }} />
                ) : undefined
              }
            >
              Create Customer & Upload to S3
            </StepLabel>
            <StepContent>{renderStep1()}</StepContent>
          </Step>

          <Step>
            <StepLabel
              icon={
                upmsResponse ? (
                  <CheckCircleIcon style={{ color: "#4caf50" }} />
                ) : undefined
              }
            >
              Ingest NewRx to UPMS
            </StepLabel>
            <StepContent>{renderStep2()}</StepContent>
          </Step>

          <Step>
            <StepLabel>Workflow Complete</StepLabel>
            <StepContent>{renderStep3()}</StepContent>
          </Step>
        </Stepper>
      </Grid>
    </Grid>
  );
};

export default UnifiedNewRxWorkflowSection;
